import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { RoleStore } from '../../../stores/role/role.state';
import { ChipModule } from 'primeng/chip';
import {
  Access,
  accessList,
  RoleAccessKey,
  roleTemplates,
} from '../../../models/rbac';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-permission-list',
  standalone: true,
  imports: [ChipModule, AlertComponent],
  templateUrl: './permission-list.component.html',
  styleUrl: './permission-list.component.scss',
})
export class PermissionListComponent implements OnInit, OnChanges {
  readonly roleStore = inject(RoleStore);
  readonly roleTemplates = roleTemplates;
  @Input() roleIds: string[] = [];
  @Input() plain = false;
  @Input() templateKey?: string;

  public accessList: RoleAccessKey[] = accessList;
  public allowedActions?: Access;

  constructor() {
    effect(() => {
      const roleState = getState(this.roleStore);
      this.setAllowedActions();
    });
  }
  ngOnInit(): void {
    this.setAllowedActions();
  }

  ngOnChanges() {
    this.setAllowedActions();
  }

  /**
   * Sets the allowed actions for the current instance based on the template key
   * or role IDs. If a valid `templateKey` is provided, it retrieves the corresponding
   * template's access list and assigns it to `allowedActions`. Otherwise, it fetches
   * actions based on the associated role IDs.
   *
   * @return {void} Does not return a value.
   */
  setAllowedActions() {
    if (this.templateKey) {
      const template = this.roleTemplates.find(
        template => template.key === this.templateKey
      );
      this.allowedActions = template?.accessList;
    } else {
      this.allowedActions = this.roleStore.actionsByRoleIds(this.roleIds);
    }
  }

  /**
   * Retrieves a string representation of allowed actions based on the current access permissions.
   * The allowed actions are filtered from a predefined list of access keys and returned as a
   * comma-separated string. If no actions are permitted, a default message is returned.
   *
   * @return {string} A comma-separated string of allowed action names or a default message indicating no permissions.
   */
  get allowedActionsAsPlain() {
    return (
      accessList
        .filter(
          (access: RoleAccessKey) => this.allowedActions?.[access.key] === true
        )
        .map(access => access.name)
        .join(', ') || 'Keine Berechtigungen'
    );
  }
}
